import { useMemo } from 'react'
import { useDocumentData } from 'react-firebase-hooks/firestore'
import { useIntl } from 'react-intl'

import { useQuery } from '@tanstack/react-query'
import { useNavigate, useSearch } from '@tanstack/react-router'

import { type JudicialRecordSearchResult, useSearchJudicialRecords } from '@/api/useSearchJudicialRecords'
import { toast } from '@/components/molecules/Toaster'
import { type JudicialResultListViewProps } from '@/components/templates/JudicialResultListView/JudicialResultListView.tsx'
import {
  type ConfigurationJudicialSnapshotData,
  DEFAULT_JUDICIAL_CONFIGURATION,
  judicialConfigurationDocument,
} from '@/model/firestore/judicialConfigurationDocument'
import { type JudicialFilter, type JudicialTab } from '@/routes/judicial.index'
import { judicialRecordsQuery } from '@/state/judicial/judicialRecords'

import { useBackToPilote } from '../actions/useBackToPilote'

import { useJudicialPreanalysisList } from './useJudicialPreanalysisList'

export const useJudicialResultListPage = (): JudicialResultListViewProps => {
  const intl = useIntl()
  const { data } = useQuery(judicialRecordsQuery())
  const { mutateAsync: searchJudicialRecords } = useSearchJudicialRecords()
  const [judicialConfig] = useDocumentData<ConfigurationJudicialSnapshotData>(judicialConfigurationDocument())
  const { filter, tab } = useSearch({ from: '/judicial/' })
  const navigate = useNavigate()
  const preanalysis = useJudicialPreanalysisList()

  const onFilterChange = async (newFilter: JudicialFilter) => {
    await navigate({
      to: '/judicial',
      search: (previous) => ({ ...previous, filter: newFilter }),
    })
  }

  const onTabChange = async (newTab: JudicialTab) => {
    await navigate({
      to: '/judicial',
      search: (previous) => ({ ...previous, tab: newTab }),
    })
  }

  const requestedResultsCount = useMemo(
    () => data?.data.reduce((acc, record) => acc + record.requestedResults, 0) ?? 0,
    [data?.data]
  )

  const pricePerSearch = judicialConfig?.pricePerSearch ?? DEFAULT_JUDICIAL_CONFIGURATION.pricePerSearch
  const pricePerRecord = judicialConfig?.pricePerRecord ?? DEFAULT_JUDICIAL_CONFIGURATION.pricePerRecord

  const onCandidateSearch = async (volunteerCodes: string) => {
    const results = await searchJudicialRecords({ volunteerCodes })

    // eslint-disable-next-line no-console
    console.log(results)
    Object.entries<JudicialRecordSearchResult>(results).forEach(([code, result]) => {
      if (result.error) {
        toast({
          title: intl.formatMessage({ id: 'judicial.results.error' }, { code }),
          description: result.error,
          variant: 'destructive',
        })
      }
    })
  }

  const onBackToPiloteClicked = useBackToPilote()

  const queriedCandidatesCount = data?.data.length ? data.data.length * 2 : 0

  return {
    records: data?.data ?? [],
    onCandidateSearch,
    onBackToPiloteClicked,
    queriedCandidatesCount,
    requestedResultsCount,
    pricePerSearch,
    pricePerRecord,
    selectedFilter: filter,
    onFilterChange,
    selectedTab: tab,
    onTabChange,
    preanalysis,
  }
}
